import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import Select from "react-select";
import "../../styles/projectStyles/TaskAndTeam.css";
import { fetchUserData, fetchTaskSubTask } from "../../services/API";

const TaskAndTeamService = forwardRef(({ data, setData }, ref) => {
  const [employeeData, setEmployeeData] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [validationError, setValidationError] = useState("");

  // Fetch employee data and task data when the component mounts
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await fetchUserData();
        const employees = response.data.filter(
          (user) => user.Job_Title === "Employee"
        );
        const formattedEmployeeData = employees.map((employee) => ({
          value: employee.ID,
          label: `${employee.First_Name} ${employee.Last_Name}`,
        }));
        setEmployeeData(formattedEmployeeData);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    const fetchTaskData = async () => {
      try {
        const response = await fetchTaskSubTask();
        setTaskData(response.data);
      } catch (error) {
        console.error("Error fetching task and subtask data:", error);
      }
    };

    fetchEmployeeData();
    fetchTaskData();
  }, []);

  // Fetch selectedTasks from localStorage during component initialization
  useEffect(() => {
    const savedTasks = localStorage.getItem("selectedTasks");
    const parsedTasks = savedTasks ? JSON.parse(savedTasks) : [];
    setData({
      ...data,
      selectedTasks: Array.isArray(parsedTasks) ? parsedTasks : [],
    });
  }, []);

  // Synchronize selectedTasks to localStorage
  useEffect(() => {
    if (data.selectedTasks) {
      localStorage.setItem(
        "selectedTasks",
        JSON.stringify(data.selectedTasks || [])
      );
    }
  }, [data.selectedTasks]);

  // Validate the selections
  const validateSelections = () => {
    const hasTask = (data.selectedTasks || []).length > 0;
    const hasSubTask = (data.selectedTasks || []).some(
      (task) => task.subtasklist && task.subtasklist.length > 0
    );
    const hasTeamMember = (data.selectedTasks || []).some(
      (task) => task.employeelist && task.employeelist.length > 0
    );

    const valid = hasTask && hasSubTask && hasTeamMember;

    if (!valid) {
      if (!hasTask) {
        setValidationError("At least one task must be selected.");
      } else if (!hasSubTask) {
        setValidationError("At least one subtask must be selected.");
      } else if (!hasTeamMember) {
        setValidationError("At least one team member must be assigned.");
      }
    } else {
      setValidationError("");
    }

    localStorage.setItem(
      "taskAndTeamServiceErrors",
      JSON.stringify({ isValid: valid })
    );

    return valid;
  };

  // Expose validateForm through useImperativeHandle
  useImperativeHandle(ref, () => ({
    validateForm: () => {
      return validateSelections();
    },
  }));

  const toggleTaskSelection = (taskId) => {
    const task = taskData.find((t) => t.Task_ID === taskId);
    const updatedTasks = data.selectedTasks?.some((t) => t.taskid === taskId)
      ? data.selectedTasks.filter((t) => t.taskid !== taskId)
      : [
          ...(data.selectedTasks || []),
          {
            taskid: task.Task_ID,
            taskname: task.Task_Name,
            subtasklist: [],
            employeelist: [],
          },
        ];
    setData({ ...data, selectedTasks: updatedTasks });
  };

  const handleSubTaskChange = (selectedOptions, taskId) => {
    const updatedTasks = data.selectedTasks?.map((t) =>
      t.taskid === taskId ? { ...t, subtasklist: selectedOptions || [] } : t
    );
    setData({ ...data, selectedTasks: updatedTasks });
  };

  const handleTeamChange = (selectedOptions, taskId) => {
    const updatedTasks = data.selectedTasks?.map((t) =>
      t.taskid === taskId ? { ...t, employeelist: selectedOptions || [] } : t
    );
    setData({ ...data, selectedTasks: updatedTasks });
  };

  return (
    <div className="task-and-team-container">
      <div className="newtasksScreen">
        <div className="newtasks-header-container">
          <h3 className="task-title">Task and Team</h3>
          <table className="task-labels-table">
            <tbody>
              <tr>
                <td className="assign-item">1. Assign Tasks</td>
                <td className="assign-item">2. Assign Sub-Tasks</td>
                <td className="assign-item">3. Assign Team</td>
              </tr>
            </tbody>
          </table>
          <div className="tasks-container">
            <table className="tasks-table">
              <tbody>
                {taskData.map((task) => (
                  <tr
                    key={task.Task_ID}
                    className={
                      data.selectedTasks?.some((t) => t.taskid === task.Task_ID)
                        ? "highlighted-row"
                        : ""
                    }
                  >
                    <td>
                      <img
                        src={
                          data.selectedTasks?.some(
                            (t) => t.taskid === task.Task_ID
                          )
                            ? "/images/active.svg"
                            : "/images/inactive.svg"
                        }
                        alt="Toggle Icon"
                        className="toggle-icon"
                        onClick={() => toggleTaskSelection(task.Task_ID)}
                      />
                      {task.Task_Name}
                    </td>
                    <td>
                      <Select
                        isMulti
                        options={task.Subtasks.map((subtask) => ({
                          value: subtask.SUBTASK_ID,
                          label: subtask.Subtask_Name,
                        }))}
                        value={
                          data.selectedTasks?.find(
                            (t) => t.taskid === task.Task_ID
                          )?.subtasklist || []
                        }
                        onChange={(selected) =>
                          handleSubTaskChange(selected, task.Task_ID)
                        }
                        placeholder="Multi Select Sub-Tasks"
                        classNamePrefix="select"
                      />
                    </td>
                    <td>
                      <Select
                        isMulti
                        options={employeeData}
                        value={
                          data.selectedTasks?.find(
                            (t) => t.taskid === task.Task_ID
                          )?.employeelist || []
                        }
                        onChange={(selected) =>
                          handleTeamChange(selected, task.Task_ID)
                        }
                        placeholder="Multi Select Team"
                        classNamePrefix="select"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {validationError && (
          <div className="validation-error">
            <span className="error-text">{validationError}</span>
          </div>
        )}
      </div>
    </div>
  );
});

export default TaskAndTeamService;
